import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import {
  BookingIntentResponse,
  CreateProviderAppointmentDto,
  PaymentCollectionStrategy,
} from '@models';
import {
  ActiveBookingIntentService,
  BookingIntentsService,
  CHECKOUT_SERVICE,
  MeService,
} from '@services';
import {
  AppointmentNoteFieldComponent,
  ButtonComponent,
  CheckoutDialogComponent,
  DatepickerComponent,
  DropdownSelectComponent,
  InputValidationMessageDirective,
  RadioButtonGroupComponent,
  RadioTimeSlotComponent,
  SelectOptionComponent,
  SetupPaymentMethodDialogComponent,
  SpinnerComponent,
} from '@ui-lib';
import { forkJoin } from 'rxjs';
import { BookAppointmentDialogBaseComponent } from './book-appointment-dialog.base';

@Component({
  selector: 'icaredx-book-appointment-patient-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SpinnerComponent,
    AsyncPipe,
    DatepickerComponent,
    MatAutocompleteModule,
    RadioButtonGroupComponent,
    RadioTimeSlotComponent,
    ButtonComponent,
    MatIconModule,
    DropdownSelectComponent,
    SelectOptionComponent,
    InputValidationMessageDirective,
    AppointmentNoteFieldComponent,
  ],
  templateUrl: './book-appointment-dialog.component.html',
})
export class BookAppointmentPatientDialogComponent extends BookAppointmentDialogBaseComponent {
  private readonly bookintIntentService = inject(BookingIntentsService);
  private readonly activeIntentService = inject(ActiveBookingIntentService);
  private readonly meService = inject(MeService);
  private readonly injector = inject(Injector);

  protected readonly actor = 'patient' as const;

  protected fetchInitialData(): void {
    this.initialLoading.set(true);

    const patientId = this.meService.userId;
    this.appointmentForm.patchValue({ patientId: patientId ?? '' });
    this.patientIdControl?.markAsPristine();

    forkJoin(this.getCommonBookingRequests())
      .pipe(this.getFetchPipeline())
      .subscribe(({ providerLanguages, appointmentTypes, timezones }) => {
        this.applyCommonBookingData({
          providerLanguages,
          appointmentTypes,
          timezones,
          providerTimezone: undefined,
        });
      });
  }

  override createNewAppointment(body: CreateProviderAppointmentDto): void {
    this.submitting.set(true);

    const provider = this.activeProvider()!;

    if (
      !this.selectedAppointmentType?.checkoutRequired ||
      !provider.canAcceptPayments
    ) {
      this.createUnpaidAppointment(body);
      return;
    }

    this.bookintIntentService
      .createBookingIntent(
        provider.providerId,
        body.appointmentTypeId,
        body.startUtc,
        body.locationType,
        body.appointmentLanguage
      )
      .subscribe(result => {
        if (result) {
          this.activeIntentService.reload();
          this.handleIntentCreated(result);
        }
      });

    return;
  }

  private handleIntentCreated(intent: BookingIntentResponse): void {
    this.submitting.set(false);

    if (this.selectedAppointmentType?.checkoutRequired) {
      const dialogData = {
        itemId: intent.intentId,
        servicesCost: this.selectedAppointmentType?.cost,
        taxPercentage: this.selectedAppointmentType?.taxPercentage,
      };

      if (
        this.selectedAppointmentType?.paymentCollectionStrategy ===
        PaymentCollectionStrategy.SetupWithoutCharging
      ) {
        const checkoutInjector = Injector.create({
          parent: this.injector,
          providers: [
            {
              provide: CHECKOUT_SERVICE,
              useValue: this.bookintIntentService,
            },
          ],
        });

        this.dialogRef.close();
        this.dialog.open(SetupPaymentMethodDialogComponent, {
          data: dialogData,
          injector: checkoutInjector,
        });

        return;
      }

      const providerId = this.activeProvider()?.providerId;
      if (!providerId) {
        return;
      }

      this.bookintIntentService
        .createCheckoutSession({
          itemId: intent.intentId,
          providerId,
        })
        .subscribe(result => {
          this.dialogRef.close();
          this.dialog.open(CheckoutDialogComponent, {
            data: {
              servicesCost: this.selectedAppointmentType?.cost,
              subtotalCents: result.subtotalCents,
              taxAmountCents: result.taxAmountCents,
              totalCents: result.totalCents,
              taxPercentage: result.taxPercentage,
              checkoutUrl: result.url,
            },
          });
        });
    }
  }
}
